import Campo, { Area } from './Campo';
import Chips from './Chips';
import { mascaraDataBr, novoRemedio } from '../utils';
import type { Crianca, Remedio } from '../types';

interface CartaoCriancaProps {
  indice: number;
  crianca: Crianca;
  erros: Record<string, string>;
  destacada: boolean;
  aoMudar: (patch: Partial<Crianca>) => void;
  aoRemover?: () => void;
}

const OPCOES_ALERGIA = [
  { valor: 'nao', rotulo: 'Não tem' },
  { valor: 'sim', rotulo: 'Tem alergia' },
];

const OPCOES_ALIMENTACAO = [
  { valor: 'nao', rotulo: 'Come de tudo' },
  { valor: 'sim', rotulo: 'Tem restrição' },
];

const OPCOES_REMEDIO = [
  { valor: 'nao', rotulo: 'Não toma' },
  { valor: 'sim', rotulo: 'Toma remédio' },
];

const OPCOES_FEBRE = [
  { valor: 'nao', rotulo: 'Não autorizo' },
  { valor: 'sim', rotulo: 'Autorizo' },
];

function simNao(valor: boolean | null): 'sim' | 'nao' | null {
  if (valor === null) return null;
  return valor ? 'sim' : 'nao';
}

export default function CartaoCrianca({
  indice,
  crianca,
  erros,
  destacada,
  aoMudar,
  aoRemover,
}: CartaoCriancaProps) {
  const e = (campo: string) => erros[`${crianca.chave}-${campo}`] ?? null;
  const linhas: Remedio[] = crianca.remedios.length > 0 ? crianca.remedios : [novoRemedio()];

  function mudarRemedio(posicao: number, campo: keyof Remedio, valor: string) {
    const base = linhas.map((linha, i) => (i === posicao ? { ...linha, [campo]: valor } : linha));
    aoMudar({ remedios: base });
  }

  function removerRemedio(posicao: number) {
    aoMudar({ remedios: linhas.filter((_, i) => i !== posicao) });
  }

  return (
    <div
      id={`crianca-${indice + 1}`}
      className={`w-full min-w-0 max-w-full overflow-hidden rounded-2xl border bg-white p-4 ${
        destacada ? 'border-danger-400' : 'border-background-200'
      }`}
    >
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <h2 className="min-w-0 break-words text-[15px] font-bold text-foreground-950">
          Criança {indice + 1}
        </h2>
        {aoRemover && (
          <button
            type="button"
            onClick={aoRemover}
            className="aperta flex h-11 shrink-0 cursor-pointer items-center whitespace-nowrap rounded-lg px-2 text-[13px] font-semibold text-danger-600"
          >
            Remover esta criança
          </button>
        )}
      </div>

      <div className="mt-3 min-w-0 space-y-4">
        <Campo
          id={`nome-crianca-${indice + 1}`}
          rotulo="Nome completo"
          obrigatorio
          valor={crianca.nome}
          aoMudar={(v) => aoMudar({ nome: v })}
          placeholder="Como está na certidão"
          autoComplete="off"
          erro={e('nome')}
        />

        <Campo
          id={`nascimento-crianca-${indice + 1}`}
          rotulo="Data de nascimento"
          obrigatorio
          valor={crianca.nascimento}
          aoMudar={(v) => aoMudar({ nascimento: mascaraDataBr(v) })}
          placeholder="dd/mm/aaaa"
          inputMode="numeric"
          maxLength={10}
          autoComplete="off"
          ajuda={
            crianca.idade && !crianca.nascimento
              ? `Temos ${crianca.idade} anos registrados. Confirme a data.`
              : undefined
          }
          erro={e('nascimento')}
        />

        <div>
          <p className="mb-1.5 text-[15px] font-semibold text-foreground-900">Alergias</p>
          <Chips
            grupo={`Alergias da criança ${indice + 1}`}
            opcoes={OPCOES_ALERGIA}
            valor={simNao(crianca.temAlergia)}
            aoEscolher={(v) => aoMudar({ temAlergia: v === 'sim' })}
          />
          {crianca.temAlergia === true && (
            <div className="mt-2">
              <Campo
                valor={crianca.alergiaTexto}
                aoMudar={(v) => aoMudar({ alergiaTexto: v })}
                placeholder="A quê? (remédio, alimento, picada de inseto…)"
                ajuda="Escreva tudo o que souber."
                erro={e('alergia')}
              />
            </div>
          )}
          {crianca.temAlergia !== true && e('alergia') && (
            <p className="mt-1.5 text-sm text-danger-600" role="alert">
              {e('alergia')}
            </p>
          )}
        </div>

        <div>
          <p className="mb-1.5 text-[15px] font-semibold text-foreground-900">Alimentação</p>
          <Chips
            grupo={`Alimentação da criança ${indice + 1}`}
            opcoes={OPCOES_ALIMENTACAO}
            valor={simNao(crianca.temRestricao)}
            aoEscolher={(v) => aoMudar({ temRestricao: v === 'sim' })}
          />
          {crianca.temRestricao === true && (
            <div className="mt-2">
              <Campo
                valor={crianca.alimentacaoTexto}
                aoMudar={(v) => aoMudar({ alimentacaoTexto: v })}
                placeholder="O que não pode comer ou precisa comer?"
                erro={e('alimentacao')}
              />
            </div>
          )}
          {crianca.temRestricao !== true && e('alimentacao') && (
            <p className="mt-1.5 text-sm text-danger-600" role="alert">
              {e('alimentacao')}
            </p>
          )}
        </div>

        <div>
          <p className="mb-1.5 text-[15px] font-semibold text-foreground-900">
            Remédios de uso contínuo
          </p>
          <Chips
            grupo={`Remédios da criança ${indice + 1}`}
            opcoes={OPCOES_REMEDIO}
            valor={simNao(crianca.tomaRemedio)}
            aoEscolher={(v) => aoMudar({ tomaRemedio: v === 'sim' })}
          />

          {crianca.tomaRemedio === true && (
            <div className="mt-3 min-w-0 space-y-3 rounded-xl bg-background-50 p-3">
              {linhas.map((linha, posicao) => (
                <div key={`${crianca.chave}-rem-${posicao}`} className="min-w-0">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1 space-y-2">
                      <Campo
                        valor={linha.nome}
                        aoMudar={(v) => mudarRemedio(posicao, 'nome', v)}
                        placeholder="Remédio"
                        ajuda={posicao === 0 ? 'Ex.: Amoxicilina' : undefined}
                      />
                      <div className="grid w-full min-w-0 grid-cols-1 gap-2 sm:grid-cols-2">
                        <Campo
                          valor={linha.dose}
                          aoMudar={(v) => mudarRemedio(posicao, 'dose', v)}
                          placeholder="Dose"
                          ajuda={posicao === 0 ? 'Ex.: 5 ml' : undefined}
                        />
                        <Campo
                          valor={linha.horarios}
                          aoMudar={(v) => mudarRemedio(posicao, 'horarios', v)}
                          placeholder="Horários"
                          ajuda={posicao === 0 ? 'Ex.: 8h e 20h' : undefined}
                        />
                      </div>
                    </div>
                    {linhas.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removerRemedio(posicao)}
                        aria-label={`Remover remédio ${posicao + 1}`}
                        className="aperta mt-1 flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-lg text-foreground-500 hover:bg-background-200"
                      >
                        <i className="ri-delete-bin-line text-lg" aria-hidden />
                      </button>
                    )}
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={() => aoMudar({ remedios: [...linhas, novoRemedio()] })}
                className="aperta flex h-11 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-lg px-1 text-[14px] font-semibold text-primary-600"
              >
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-add-line" aria-hidden />
                </span>
                Outro remédio
              </button>

              {e('remedios') && (
                <p className="text-sm text-danger-600" role="alert">
                  {e('remedios')}
                </p>
              )}
            </div>
          )}

          {crianca.tomaRemedio !== true && e('remedios') && (
            <p className="mt-1.5 text-sm text-danger-600" role="alert">
              {e('remedios')}
            </p>
          )}
        </div>

        <div>
          <p className="mb-1.5 text-[15px] font-semibold text-foreground-900">
            Se tiver febre ou dor, podemos dar remédio?
          </p>
          <Chips
            grupo={`Autorização de remédio para febre ou dor, criança ${indice + 1}`}
            opcoes={OPCOES_FEBRE}
            valor={simNao(crianca.autorizaFebre)}
            aoEscolher={(v) => aoMudar({ autorizaFebre: v === 'sim' })}
          />
          {crianca.autorizaFebre === true && (
            <div className="mt-2">
              <Campo
                valor={crianca.febreTexto}
                aoMudar={(v) => aoMudar({ febreTexto: v })}
                placeholder="Qual remédio e quanto? (ex.: Paracetamol gotas, 15 gotas)"
                erro={e('febre')}
              />
            </div>
          )}
          {crianca.autorizaFebre !== true && e('febre') && (
            <p className="mt-1.5 text-sm text-danger-600" role="alert">
              {e('febre')}
            </p>
          )}
        </div>

        <Area
          id={`saude-crianca-${indice + 1}`}
          rotulo="Saúde e cuidados (opcional)"
          valor={crianca.saude}
          aoMudar={(v) => aoMudar({ saude: v })}
          placeholder="Alguma condição de saúde, necessidade especial, medo, se sabe nadar, se usa fralda…"
          linhas={3}
        />
      </div>
    </div>
  );
}